import { FunctionComponent, useState, useCallback, ChangeEvent, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Modal, Icon } from "@/components";
import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	useGetFieldMappingsQuery,
	useGetHubspotContactFieldsQuery,
	useGetHubspotCompanyFieldsQuery,
	useGetHubspotDealFieldsQuery,
	useGetHubspotLeadFieldsQuery,
	useGetSourceFieldsQuery,
	useCreateFieldMappingMutation,
	useUpdateFieldMappingMutation,
	useDeleteFieldMappingMutation,
	useTestHubspotAiFieldMappingMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";
import { useLazyGetContactsQuery } from "@/pages/Private/redux/contact/contact.api";

import {
	HubspotEntityType,
	FieldMapping,
	FieldMappingAiConfig,
	FieldMappingSourceType,
	HubspotField,
	SourceField,
} from "../schema/hubspot";

import { HubspotMappingRow, HubspotNewMappingRow, NewHubspotMapping } from "./HubspotMappingRow";
import {
	buildUsedFields,
	getApiErrorMessage,
	isHubspotMissingLeadScopeError,
} from "./hubspotFieldMapping.utils";
import {
	AiConfigModal,
	buildAiConfigLabels,
	buildAiProspectOptions,
	FieldMappingFooter,
} from "./fieldMapping";

enum Tabs {
	CONTACTS = "CONTACTS",
	COMPANIES = "COMPANIES",
	DEALS = "DEALS",
	LEADS = "LEADS",
}

interface HubspotFieldMappingModalProps {
	isOpen: boolean;
	onClose: () => void;
}

interface HubspotAiConfigSelection {
	id?: number;
	tempId?: string;
	label: string;
	targetField: HubspotField;
	allFields: HubspotField[];
	entityType: HubspotEntityType;
	aiConfig?: FieldMappingAiConfig;
}

export const HubspotFieldMappingModal: FunctionComponent<HubspotFieldMappingModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>): string =>
		t(`company.${key}`, options as never) as unknown as string;
	const tsRow = (key: string, options?: Record<string, unknown>): string =>
		ts(`hubspot.fieldMapping.${key}`, options);

	const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.CONTACTS);
	const [newMappings, setNewMappings] = useState<NewHubspotMapping[]>([]);
	const [savingTempId, setSavingTempId] = useState<string | null>(null);
	const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
	const [aiConfigTarget, setAiConfigTarget] = useState<HubspotAiConfigSelection | null>(null);
	const aiProspectSearchRequestRef = useRef<{ abort?: () => void } | null>(null);

	const entityTypeByTab = {
		[Tabs.CONTACTS]: HubspotEntityType.CONTACT,
		[Tabs.COMPANIES]: HubspotEntityType.COMPANY,
		[Tabs.DEALS]: HubspotEntityType.DEAL,
		[Tabs.LEADS]: HubspotEntityType.LEAD,
	};
	const entityType = entityTypeByTab[currentTab];

	// Fetch data
	const { data: contactMappings, isLoading: isLoadingContactMappings } = useGetFieldMappingsQuery({
		entityType: HubspotEntityType.CONTACT,
	});
	const { data: companyMappings, isLoading: isLoadingCompanyMappings } = useGetFieldMappingsQuery({
		entityType: HubspotEntityType.COMPANY,
	});
	const { data: dealMappings, isLoading: isLoadingDealMappings } = useGetFieldMappingsQuery({
		entityType: HubspotEntityType.DEAL,
	});
	const { data: leadMappings, isLoading: isLoadingLeadMappings } = useGetFieldMappingsQuery({
		entityType: HubspotEntityType.LEAD,
	});
	const { data: contactFields } = useGetHubspotContactFieldsQuery();
	const { data: companyFields } = useGetHubspotCompanyFieldsQuery();
	const { data: dealFields } = useGetHubspotDealFieldsQuery();
	const { data: leadFields, error: leadFieldsError } = useGetHubspotLeadFieldsQuery();
	const { data: sourceFields } = useGetSourceFieldsQuery();

	// Mutations
	const [createMapping] = useCreateFieldMappingMutation();
	const [updateMapping] = useUpdateFieldMappingMutation();
	const [deleteMapping] = useDeleteFieldMappingMutation();
	const [testAiMapping] = useTestHubspotAiFieldMappingMutation();
	const [searchProspects] = useLazyGetContactsQuery();

	const contactUsed = useMemo(
		() => buildUsedFields(contactMappings, newMappings, HubspotEntityType.CONTACT),
		[contactMappings, newMappings]
	);
	const companyUsed = useMemo(
		() => buildUsedFields(companyMappings, newMappings, HubspotEntityType.COMPANY),
		[companyMappings, newMappings]
	);
	const dealUsed = useMemo(
		() => buildUsedFields(dealMappings, newMappings, HubspotEntityType.DEAL),
		[dealMappings, newMappings]
	);
	const leadUsed = useMemo(
		() => buildUsedFields(leadMappings, newMappings, HubspotEntityType.LEAD),
		[leadMappings, newMappings]
	);

	const handleTabsChange = useCallback((_: ChangeEvent<{}>, value: Tabs): void => {
		setCurrentTab(value);
	}, []);

	const handleAddMapping = () => {
		const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		setNewMappings((prev) => [
			...prev,
			{
				tempId,
				sourceField: "",
				sourceType: FieldMappingSourceType.FIELD,
				hubspotField: "",
				isActive: true,
				entityType,
			},
		]);
	};

	const clearSaveError = (tempId: string) => {
		setSaveErrors((prev) => {
			if (!(tempId in prev)) {
				return prev;
			}

			const next = { ...prev };

			delete next[tempId];

			return next;
		});
	};

	const handleUpdateNewMapping = (tempId: string, data: Partial<NewHubspotMapping>) => {
		setNewMappings((prev) => prev.map((m) => (m.tempId === tempId ? { ...m, ...data } : m)));
		clearSaveError(tempId);
	};

	const handleConfigureAi = (
		mapping: {
			id?: number;
			tempId?: string;
			hubspotField: string;
			entityType: HubspotEntityType;
			aiConfig?: FieldMappingAiConfig;
		},
		fields: HubspotField[]
	) => {
		const target = fields.find((f) => f.name === mapping.hubspotField) ?? {
			name: mapping.hubspotField,
			label: mapping.hubspotField,
			type: "string",
			fieldType: "text",
			isCustom: false,
		};

		setAiConfigTarget({
			id: mapping.id,
			tempId: mapping.tempId,
			label: target.label,
			targetField: target,
			allFields: fields,
			entityType: mapping.entityType,
			aiConfig: mapping.aiConfig,
		});
	};

	const handleSearchAiProspects = async (query: string) => {
		aiProspectSearchRequestRef.current?.abort?.();
		const request = searchProspects({ page: 1, limit: 10, searchValue: query });

		aiProspectSearchRequestRef.current = request;

		try {
			const result = await request.unwrap();

			return buildAiProspectOptions(result.data);
		} catch {
			return [];
		} finally {
			if (aiProspectSearchRequestRef.current === request) {
				aiProspectSearchRequestRef.current = null;
			}
		}
	};

	const handleCancelAiProspectSearch = () => {
		aiProspectSearchRequestRef.current?.abort?.();
		aiProspectSearchRequestRef.current = null;
	};

	const handleSaveAiConfig = async (config: FieldMappingAiConfig) => {
		if (!aiConfigTarget) {
			return;
		}

		if (aiConfigTarget.tempId) {
			handleUpdateNewMapping(aiConfigTarget.tempId, {
				aiConfig: config,
				sourceField: aiConfigTarget.label,
			});
		} else if (aiConfigTarget.id) {
			await updateMapping({
				id: aiConfigTarget.id,
				data: {
					sourceType: FieldMappingSourceType.AI,
					sourceField: aiConfigTarget.label,
					aiConfig: config,
				},
			}).unwrap();
		}

		setAiConfigTarget(null);
	};

	const handleSaveNewMapping = async (mapping: NewHubspotMapping): Promise<void> => {
		if (
			!mapping.hubspotField ||
			(mapping.sourceType !== FieldMappingSourceType.AI && !mapping.sourceField) ||
			(mapping.sourceType === FieldMappingSourceType.AI && !mapping.aiConfig?.prompt)
		) {
			return;
		}

		setSavingTempId(mapping.tempId);
		try {
			await createMapping({
				sourceField: mapping.sourceField || mapping.hubspotField,
				sourceType: mapping.sourceType,
				hubspotField: mapping.hubspotField,
				entityType: mapping.entityType,
				isActive: mapping.isActive,
				aiConfig: mapping.aiConfig,
			}).unwrap();
			// Remove from local state on success
			setNewMappings((prev) => prev.filter((m) => m.tempId !== mapping.tempId));
			clearSaveError(mapping.tempId);
		} catch (error) {
			// PREC-3967: the API rejects a static value the target enumeration does not offer —
			// show its reason on the row instead of losing it in the console.
			setSaveErrors((prev) => ({
				...prev,
				[mapping.tempId]: getApiErrorMessage(error, tsRow("saveFailed")),
			}));
		} finally {
			setSavingTempId(null);
		}
	};

	const handleCancelNewMapping = (tempId: string) => {
		setNewMappings((prev) => prev.filter((m) => m.tempId !== tempId));
		clearSaveError(tempId);
	};

	const handleToggleActive = async (id: number, isActive: boolean) => {
		await updateMapping({ id, data: { isActive } });
	};

	const handleDeleteMapping = async (id: number) => {
		await deleteMapping(id);
	};

	const isLoading =
		isLoadingContactMappings ||
		isLoadingCompanyMappings ||
		isLoadingDealMappings ||
		isLoadingLeadMappings;

	const renderTabContent = (
		entity: HubspotEntityType,
		mappings: FieldMapping[] | undefined,
		fields: HubspotField[],
		entitySourceFields: SourceField[] | undefined,
		used: {
			usedSourceFields: string[];
			usedHubspotFieldsByType: Record<FieldMappingSourceType, string[]>;
		}
	) => {
		const tabNewMappings = newMappings.filter((m) => m.entityType === entity);

		return (
			<div className="mt-2">
				<div className="flex items-center gap-3 py-3 border-b border-gray-200 font-medium text-sm text-gray-600">
					<div className="flex-1">{ts("hubspot.fieldMapping.ourField")}</div>
					<div className="w-[24px]"></div>
					<div className="flex-1">{ts("hubspot.fieldMapping.hubspotField")}</div>
					<div className="w-[60px] text-center">{ts("hubspot.fieldMapping.active")}</div>
					<div className="w-[80px]"></div>
				</div>

				{isLoading ? (
					<LoadingBlock className="py-8" />
				) : (
					<>
						{mappings?.map((mapping) => (
							<HubspotMappingRow
								key={mapping.id}
								hubspotFields={fields}
								mapping={mapping}
								sourceFields={entitySourceFields || []}
								ts={tsRow}
								onConfigureAi={(m) => handleConfigureAi(m, fields)}
								onDelete={handleDeleteMapping}
								onToggleActive={handleToggleActive}
							/>
						))}
						{tabNewMappings.map((mapping) => (
							<HubspotNewMappingRow
								key={mapping.tempId}
								hubspotFields={fields}
								isSaving={savingTempId === mapping.tempId}
								mapping={mapping}
								saveError={saveErrors[mapping.tempId]}
								sourceFields={entitySourceFields || []}
								ts={tsRow}
								usedHubspotFieldsByType={used.usedHubspotFieldsByType}
								usedSourceFields={used.usedSourceFields}
								onCancel={handleCancelNewMapping}
								onConfigureAi={(m) => handleConfigureAi(m, fields)}
								onSave={handleSaveNewMapping}
								onUpdate={handleUpdateNewMapping}
							/>
						))}
						{!mappings?.length && !tabNewMappings.length && (
							<div className="py-6 text-center text-gray-500">
								{ts("hubspot.fieldMapping.noMappings")}
							</div>
						)}
					</>
				)}

				<div className="mt-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-[16px] h-[16px] text-gray-700 mr-2" icon={faPlus} />}
						size={ButtonSize.S}
						title={ts("hubspot.fieldMapping.addMapping")}
						onClick={handleAddMapping}
					/>
				</div>

				{(() => {
					const aiCount =
						(mappings?.filter((m) => m.sourceType === FieldMappingSourceType.AI).length ?? 0) +
						tabNewMappings.filter((m) => m.sourceType === FieldMappingSourceType.AI).length;
					const mappedCount = (mappings?.length ?? 0) + tabNewMappings.length;

					return (
						<FieldMappingFooter
							aiCount={aiCount}
							creditPillLabel={tsRow("footerCreditPill")}
							mappedCount={mappedCount}
							summaryLabel={tsRow("footerSummary", { mapped: mappedCount, ai: aiCount })}
							testIdPrefix="hubspot"
						/>
					);
				})()}
			</div>
		);
	};

	return (
		<Modal
			handleClose={onClose}
			isOpened={isOpen}
			size="lg"
			title={ts("hubspot.fieldMapping.title")}
		>
			<div className="p-4">
				<TabContext value={currentTab}>
					<TabList onChange={handleTabsChange}>
						<Tab label={ts("hubspot.fieldMapping.contacts")} value={Tabs.CONTACTS} />
						<Tab label={ts("hubspot.fieldMapping.companies")} value={Tabs.COMPANIES} />
						<Tab label={ts("hubspot.fieldMapping.deals")} value={Tabs.DEALS} />
						<Tab label={ts("hubspot.fieldMapping.leads")} value={Tabs.LEADS} />
					</TabList>

					<TabPanel sx={{ p: 0 }} value={Tabs.CONTACTS}>
						{renderTabContent(
							HubspotEntityType.CONTACT,
							contactMappings,
							contactFields?.fields || [],
							sourceFields?.contactFields,
							contactUsed
						)}
					</TabPanel>

					<TabPanel sx={{ p: 0 }} value={Tabs.COMPANIES}>
						{renderTabContent(
							HubspotEntityType.COMPANY,
							companyMappings,
							companyFields?.fields || [],
							sourceFields?.companyFields,
							companyUsed
						)}
					</TabPanel>

					<TabPanel sx={{ p: 0 }} value={Tabs.DEALS}>
						{renderTabContent(
							HubspotEntityType.DEAL,
							dealMappings,
							dealFields?.fields || [],
							sourceFields?.dealFields,
							dealUsed
						)}
					</TabPanel>
					<TabPanel sx={{ p: 0 }} value={Tabs.LEADS}>
						{isHubspotMissingLeadScopeError(leadFieldsError) && (
							<div
								className="mt-3 rounded-lg border border-status-warning bg-status-warning-light p-3 text-sm text-text"
								data-testid="hubspot-leads-scope-hint"
							>
								{tsRow("leadsScopeHint")}
							</div>
						)}
						{renderTabContent(
							HubspotEntityType.LEAD,
							leadMappings,
							leadFields?.fields || [],
							sourceFields?.leadFields,
							leadUsed
						)}
					</TabPanel>
				</TabContext>
			</div>
			<AiConfigModal
				allFields={aiConfigTarget?.allFields ?? []}
				callbacks={{
					onCancelSearchProspects: handleCancelAiProspectSearch,
					onSearchProspects: handleSearchAiProspects,
					onRunTest: (prospectId, prompt, fallbackValue) =>
						testAiMapping({
							prospectId,
							entityType: aiConfigTarget?.entityType ?? entityType,
							hubspotField: aiConfigTarget?.targetField.name,
							prompt,
							allowedValues: aiConfigTarget?.targetField.options?.map((option) => option.value),
							fallbackValue,
						}).unwrap(),
				}}
				initialConfig={aiConfigTarget?.aiConfig}
				isOpen={Boolean(aiConfigTarget)}
				labels={buildAiConfigLabels(tsRow)}
				subtitle={ts("hubspot.fieldMapping.aiConfigSubtitle", {
					label: aiConfigTarget?.label ?? "",
				})}
				targetField={aiConfigTarget?.targetField}
				title={ts("hubspot.fieldMapping.aiConfigTitle")}
				onClose={() => setAiConfigTarget(null)}
				onSave={handleSaveAiConfig}
			/>
		</Modal>
	);
};
